'use client'

import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { InformationIcon, StatusWarningIcon } from '@dfds-frontend/navigator-icons/icons'

interface Props {
  type: 'warning' | 'error' | 'info'
  message: string
  onClose: () => void
}

export const DepartureSelectionsAlert: FC<Props> = ({ message, onClose, type }) => {
  const { t } = useTranslation()
  const [displayMessage, setDisplayMessage] = useState(message || '')
  const [isVisible, setIsVisible] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Only render the portal on the client, after mount, to avoid SSR hydration mismatches.
  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (message) {
      setDisplayMessage(message)
      setIsVisible(true)
      const timer = setTimeout(() => {
        onClose()
      }, 3000) // Auto close after 3 seconds

      return () => clearTimeout(timer)
    }
  }, [message, onClose])

  useEffect(() => {
    if (!message && isVisible) {
      setIsVisible(false)
      const timer = setTimeout(() => {
        setDisplayMessage('')
      }, 300) // Wait for fade-out animation to complete

      return () => clearTimeout(timer)
    }
  }, [message, isVisible])

  if (!mounted) return null

  // Render into document.body via a portal so the fixed overlay always sits at the top of the
  // viewport stacking context, regardless of where <AlertDialog /> is mounted in the tree.
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex w-full justify-center p-4 lg:top-15.5 lg:bottom-auto">
      <div className="mb-20 flex w-full justify-center lg:mb-0">
        <div
          className={clsx(
            'pointer-events-auto w-full transform cursor-pointer overflow-hidden border p-6 text-left align-middle shadow-xl lg:w-fit',
            'transition-all duration-300 ease-in-out',
            {
              'border-semantic-global-danger-dark bg-semantic-global-danger-lightest': type === 'error',
              'border-semantic-global-info-darker bg-semantic-global-info-lightest': type === 'info',
              'border-semantic-global-warning-default bg-semantic-global-warning-lightest': type === 'warning',
              'translate-y-0 opacity-100': isVisible,
              'translate-y-4 opacity-0 lg:-translate-y-4': !isVisible,
            }
          )}
          data-e2e={`alert-dialog-${type}`}
          onClick={onClose}
        >
          <div className="gap-semantic-global-md flex w-full">
            {type === 'info' ? <InformationIcon size="lg" /> : <StatusWarningIcon size="lg" />}
            <p className="typography-body-md-regular">{t(displayMessage)}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
